import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { TranscriberStateEnum } from '../../_enums/transcriber-state.enum';
import { DiarizationResponse } from '../../_models/diarization-response.model';
import { TaskService } from '../../_services/task.service';
import { WorkProcessWithClient } from '../../_models/social-domain/workprocess-with-client.model';
import { MatIconModule } from '@angular/material/icon';
import { TemplateCategoryEnum } from '../../_enums/template-category.enum';
import { TemplateEntry } from '../../_models/template-entry.model';
import { TemplateCatalogService } from '../../_services/template-catalog.service';
import { FormsModule } from '@angular/forms';
import { Document, Paragraph, TextRun, Packer } from 'docx';
import { from, map, Observable, shareReplay } from 'rxjs';
import { DocumentService } from '../../_services/document.service';
import { DocumentTypeEnum } from '../../_enums/document-type.enum';
import { WorkflowService } from '../../_services/workflow.service';
import { TranscriberConfigService } from '../../_services/transcriber-config.service';
import { Environment } from '../../../environment/environment.model';
import { InlineSnackbar } from '../inline-snackbar/inline-snackbar';
import { SnackbarType } from '../../_enums/snackbar-type.enum';
import { TranscriberPhaseEnum } from '../../_enums/transcriber-phase.enum';
import { Waiting } from '../waiting/waiting';
import { WorkProcessSelector } from '../work-process-selector/work-process-selector';
import { DocxPreview } from '../document-editor/docx-preview';
import { UploadDocument } from '../upload-document/upload-document';
import { buildDocumentFilename } from '../../_helpers/document-filename.util';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const SUMMARY_FILE_PREFIX = 'gespreksverslag';

// Het Azure-spraaktoken van de widget is 10 minuten geldig; ruim daarvoor verversen we.
const SPEECH_TOKEN_REFRESH_MS = 9 * 60 * 1000;

export interface TranscriberSelection {
  workProcess: WorkProcessWithClient | null;
}

@Component({
  selector: 'app-transcriber-content',
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslateModule,
    MatIconModule,
    FormsModule,
    InlineSnackbar,
    Waiting,
    WorkProcessSelector,
    UploadDocument,
  ],
  templateUrl: './transcriber-content.html',
  styleUrl: './transcriber-content.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TranscriberContent implements OnInit, OnDestroy {
  /**
   * De transcriber-bibliotheek bewaart de opname in services die alle <mynte-transcriber>-
   * elementen op de pagina delen en die pas bij een paginaherlading verdwijnen. Een nieuw
   * element begint dus met het transcript van het vorige gesprek. Die vlag moet daarom net
   * zo lang leven als de pagina, niet als dit component.
   */
  private static recordingStartedOnThisPage = false;

  // De werkproceskeuze wordt direct doorgegeven; de vervolgstappen gebruiken die
  // zodra de gebruiker daar het actieplan maakt of uploadt.
  @Output() selectionChanged = new EventEmitter<TranscriberSelection>();
  // Er is een transcript én daar is een samenvatting voor gegenereerd.
  @Output() summaryGenerated = new EventEmitter<void>();
  // De samenvatting als .docx, klaar om naar Suites te uploaden.
  @Output() uploadSummaryToSsd = new EventEmitter<File>();

  @Input() isSummaryUploadInProgress = false;
  @Input() summaryUploadDone = false;

  @ViewChild('transcriberRef') transcriberRef?: ElementRef<HTMLElement>;

  /**
   * De widget haalt zijn spraaktoken één keer op, bij het aanmaken, en ververst het in
   * bibliotheekmodus nooit. Na 10 minuten neemt hij dan wel op (de timer loopt), maar komt
   * er geen tekst. Daarom maken we hem pas aan bij het gesprek, en zolang er nog niet
   * opgenomen wordt maken we hem op tijd opnieuw aan: dat haalt een vers token op.
   */
  public isTranscriberMounted = false;
  private tokenRefreshTimer: ReturnType<typeof setTimeout> | undefined;

  public xApiKey = '';
  public assistantId: string;
  public tenantId: string;
  public apiPath: string;

  constructor(@Inject('env') private environment: Environment) {
    this.assistantId = this.environment.transcriber.assistantId;
    this.tenantId = this.environment.transcriber.tenantId;
    this.apiPath = this.environment.transcriber.apiPath;
  }

  // Comes from the tenant catalogue (see loadTemplates).
  public conversationTypes: TemplateEntry[] = [];
  public templatesError = false;

  public selectedWorkProcess: WorkProcessWithClient | null = null;
  public selectedConversationType: TemplateEntry | null = null;

  public phase = TranscriberPhaseEnum.SETUP;
  // De verste fase die bereikt is. Eerdere fasen blijven bereikbaar om terug te kijken,
  // maar zijn daar alleen-lezen: die keuzes horen bij het gesprek dat al loopt.
  public furthestPhase = TranscriberPhaseEnum.SETUP;
  public readonly TranscriberPhaseEnum = TranscriberPhaseEnum;
  public readonly phases = [
    TranscriberPhaseEnum.SETUP,
    TranscriberPhaseEnum.RECORDING,
    TranscriberPhaseEnum.SUMMARY,
  ];
  public phaseTranslationKeyPrefix = 'transcriber.phases.';

  public transcriptId: string | undefined;
  public transcriptSegments: DiarizationResponse[] = [];
  public isSummaryBtnDisabled = true;
  public actionPlanFile: Blob | undefined;
  // Het gespreksverslag dat Mynte invult: dat is wat de gebruiker nakijkt en wat naar Suites gaat.
  public summaryFile: File | null = null;
  // Titel van dat verslag in de editor: het gesprekstype, bijvoorbeeld "Werkintakegesprek".
  private summaryTitle: string | undefined;
  private pendingSummary$: Observable<File> | null = null;
  private pendingTitle: string | undefined;
  public hasNoAudioWarning = false;
  public configError = false;
  public isSummarizing = false;
  public summaryError = false;

  // Een transcript dat de gebruiker zelf aanlevert in plaats van een gesprek op te nemen.
  // Zodra de opname loopt is dit niet meer aan de orde; dan telt wat er opgenomen is.
  public uploadedTranscript: File | undefined;

  public readonly SnackbarType = SnackbarType;
  public readonly DocumentTypeEnum = DocumentTypeEnum;

  private readonly taskService = inject(TaskService);
  private readonly documentService = inject(DocumentService);
  private readonly workflowService = inject(WorkflowService);
  private readonly transcriberConfigService = inject(TranscriberConfigService);
  private readonly templateCatalogService = inject(TemplateCatalogService);
  private readonly docxPreview = inject(DocxPreview);

  public onWorkProcessSelected(workProcess: WorkProcessWithClient): void {
    this.selectedWorkProcess = workProcess;
    this.onDropdownChange();
  }

  // Zonder gesprekstype kan de transcriber niets: dat is de enige harde voorwaarde
  // om het gesprek te beginnen. Het werkproces is pas later nodig.
  public get canStartConversation(): boolean {
    return !!this.selectedConversationType;
  }

  public goToRecording(): void {
    if (!this.canStartConversation) return;

    this.advanceTo(TranscriberPhaseEnum.RECORDING);
    this.mountTranscriber();
  }

  // Echt teruggaan (de voorbereiding weer bewerkbaar) mag alleen zolang er nog niets is
  // opgenomen; daarna kun je nog wel terugkijken, maar niet meer wijzigen.
  public get canReturnToSetup(): boolean {
    return this.phase === TranscriberPhaseEnum.RECORDING && !this.transcriptId;
  }

  // Uploaden is een alternatief voor opnemen, dus het kan alleen zolang er nog niets is
  // opgenomen: daarna gaat het gesprek verder met de opname.
  public get canUploadTranscript(): boolean {
    return this.phase === TranscriberPhaseEnum.RECORDING && !this.isReadOnly && !this.transcriptId;
  }

  public get hasUploadedTranscript(): boolean {
    return this.canUploadTranscript && !!this.uploadedTranscript;
  }

  // Met een aangeleverd transcript is de opname niet meer nodig: dit gaat rechtstreeks naar
  // dezelfde samenvattingstaak als een opgenomen gesprek.
  public useUploadedTranscript(): void {
    if (!this.uploadedTranscript) return;

    this.advanceTo(TranscriberPhaseEnum.SUMMARY);
    this.summarize(this.uploadedTranscript);
  }

  public backToSetup(): void {
    if (!this.canReturnToSetup) return;

    this.phase = TranscriberPhaseEnum.SETUP;
    this.furthestPhase = TranscriberPhaseEnum.SETUP;
    this.unmountTranscriber();
  }

  // Je kijkt naar een fase die je al achter je hebt gelaten.
  public get isReadOnly(): boolean {
    return this.phase !== this.furthestPhase;
  }

  public isPhaseReachable(phase: TranscriberPhaseEnum): boolean {
    return this.phases.indexOf(phase) <= this.phases.indexOf(this.furthestPhase);
  }

  public goToPhase(phase: TranscriberPhaseEnum): void {
    if (!this.isPhaseReachable(phase)) return;

    this.phase = phase;
  }

  public returnToCurrentPhase(): void {
    this.phase = this.furthestPhase;
  }

  public get furthestPhaseLabel(): string {
    return this.phaseTranslationKeyPrefix + this.furthestPhase;
  }

  /**
   * Een nieuw gesprek moet echt schoon beginnen: de transcriber-widget houdt zijn eigen
   * opname en transcript vast, en de vervolgstappen hebben documenten van dit gesprek.
   * De workflow-reset bouwt de hele stap opnieuw op, dus daar sluiten we bij aan.
   *
   * Is er op deze pagina al opgenomen, dan is dat niet genoeg: het oude transcript zit in
   * de gedeelde services van de bibliotheek (zie recordingStartedOnThisPage). Alleen een
   * herlading maakt die leeg; de bibliotheek doet zelf precies hetzelfde.
   */
  public startNewConversation(): void {
    if (TranscriberContent.recordingStartedOnThisPage) {
      // Bewust weggaan: geen 'wijzigingen gaan verloren'-melding van de browser.
      this.workflowService.setStepInProgress(false);
      this.reloadPage();
      return;
    }

    this.workflowService.triggerReset();
  }

  // Apart, zodat tests de herlading kunnen onderscheppen.
  protected reloadPage(): void {
    window.location.reload();
  }

  public ngOnDestroy(): void {
    clearTimeout(this.tokenRefreshTimer);
  }

  // Opnieuw aanmaken in een volgende tick, zodat Angular het oude element eerst opruimt
  // en het nieuwe zijn eigen token en transcript ophaalt.
  private mountTranscriber(): void {
    this.unmountTranscriber();
    setTimeout(() => {
      this.isTranscriberMounted = true;
      requestAnimationFrame(() => this.patchTranscriberStyles());
      this.tokenRefreshTimer = setTimeout(() => this.mountTranscriber(), SPEECH_TOKEN_REFRESH_MS);
    });
  }

  private unmountTranscriber(): void {
    clearTimeout(this.tokenRefreshTimer);
    this.tokenRefreshTimer = undefined;
    this.isTranscriberMounted = false;
  }

  private advanceTo(phase: TranscriberPhaseEnum): void {
    this.phase = phase;
    if (this.phases.indexOf(phase) > this.phases.indexOf(this.furthestPhase)) {
      this.furthestPhase = phase;
    }
  }

  // Tijdens het gesprek is het gekozen dossier niet meer in beeld; deze regel houdt
  // zichtbaar waar de opname bij hoort.
  public get workProcessLabel(): string {
    if (!this.selectedWorkProcess) return '';

    const client = this.selectedWorkProcess.client;
    return client
      ? `${client.naam}, ${this.selectedWorkProcess.werkprocesNummer}`
      : `${this.selectedWorkProcess.werkprocesNummer}`;
  }

  public get isSummaryUploadDisabled(): boolean {
    return (
      !this.summaryFile ||
      !this.selectedWorkProcess ||
      this.isSummaryUploadInProgress ||
      this.summaryUploadDone
    );
  }

  // Afgerond is wat je achter je hebt gelaten — niet wat je op dit moment bekijkt, want
  // terugbladeren maakt een fase niet ongedaan.
  public isPhaseDone(phase: TranscriberPhaseEnum): boolean {
    return this.phases.indexOf(phase) < this.phases.indexOf(this.furthestPhase);
  }

  public onDropdownChange(): void {
    this.workflowService.setStepInProgress(true);
    this.selectionChanged.emit({ workProcess: this.selectedWorkProcess });
  }

  public ngOnInit(): void {
    this.loadTemplates();

    // De uploadknop schrijft naar dezelfde documentenvoorraad als de andere stappen, dus
    // daar lezen we ook of er een transcript klaarstaat.
    this.documentService.getDocument(DocumentTypeEnum.TRANSCRIPT).subscribe((file) => {
      this.uploadedTranscript = file;
    });

    this.transcriberConfigService.getConfig().subscribe({
      next: (config) => {
        this.xApiKey = config.xApiKey;
        // Kwam de configuratie pas binnen nadat het gesprek al begon, dan verschijnt de
        // widget nu pas en moet hij alsnog zijn stijlaanpassingen krijgen.
        if (this.isTranscriberMounted) {
          requestAnimationFrame(() => this.patchTranscriberStyles());
        }
      },
      error: () => {
        this.configError = true;
      },
    });
  }

  // The conversation types differ per tenant, so they come from the backend catalogue.
  private loadTemplates(): void {
    this.templateCatalogService.byCategory(TemplateCategoryEnum.CONVERSATION).subscribe({
      next: (conversations) => {
        this.conversationTypes = conversations;
      },
      error: () => {
        this.templatesError = true;
      },
    });
  }

  /**
   * Overrides styles inside `<mynte-transcriber>`'s shadow root that we cannot reach with
   * `::part()`, because the elements involved carry no `part` attribute:
   * - `<encapsulated-transcriber>` sets `overflow-y: auto` and `height: 100%` on its own host,
   *   making it scroll internally. We want it to grow with its content and let the page scroll,
   *   which also removes the spurious horizontal scrollbar (per the CSS overflow spec an
   *   `overflow-y` of `auto` with an unset `overflow-x` makes `overflow-x` compute to `auto`).
   * - `.transcript-top` paints a white (`--greyscale-0`) background, which clashes with our
   *   step background.
   */
  private patchTranscriberStyles(attempt = 0): void {
    const shadowRoot = this.transcriberRef?.nativeElement.shadowRoot;

    if (shadowRoot) {
      const style = document.createElement('style');
      style.textContent = `
        encapsulated-transcriber {
          overflow: visible !important;
          height: auto !important;
          background: none !important;
        }

        encapsulated-transcriber .transcript-top,
        encapsulated-transcriber app-timer-with-bars .timer-container {
          background: none !important;
        }

        /*
         * These are 'width: 100%' plus horizontal padding, so they overflow their container by
         * the padding: our global 'box-sizing: border-box' reset does not cross the shadow
         * boundary, leaving them content-box in here.
         */
        encapsulated-transcriber .middle-container,
        encapsulated-transcriber .bottom-container {
          box-sizing: border-box !important;
        }

        /*
         * The equalizer renders ~60 fixed-width bars with fixed gaps, giving '.wave-container'
         * an intrinsic min width of 800px+. Flex items default to 'min-width: auto'
         * (= min-content), so when the timer appears that floor propagates up and shoves the
         * whole layout sideways. 'min-width: 0' stops the propagation; 'flex-shrink: 0' keeps
         * the bars at their designed 2px so they overflow and get clipped by
         * '.timer-container''s own 'overflow: hidden' instead of being squished.
         */
        encapsulated-transcriber app-timer-with-bars .wave-container {
          min-width: 0 !important;
        }

        encapsulated-transcriber app-timer-with-bars .wave-container .bar {
          flex-shrink: 0 !important;
        }

        /*
         * The record button's label changes with state (start/pause/resume) and the columns are
         * centered as a group, so a label width change slides both buttons sideways. A fixed
         * equal width per column keeps the buttons put; labels center (and wrap) within it.
         */
        encapsulated-transcriber app-recording-buttons .controls .button-with-label {
          width: 110px !important;
        }

        /*
         * Once transcript segments arrive, the library flips the header into 'compact' mode
         * (a scroll-position heuristic designed around its internal scrolling, which we
         * removed), switching the header from centered to left-aligned — a horizontal lurch.
         * Keep the header centered in both modes; the compact font sizing is left as is.
         */
        encapsulated-transcriber .transcript-top.compact {
          align-items: center !important;
        }

        encapsulated-transcriber .transcript-top.compact .logo {
          justify-content: center !important;
        }

        @media only screen and (max-width: 786px) {
          encapsulated-transcriber app-recording-buttons .controls {
            position: static !important;
            background: none !important;
          }
        }
      `;
      shadowRoot.appendChild(style);
      return;
    }

    if (attempt < 20) {
      requestAnimationFrame(() => this.patchTranscriberStyles(attempt + 1));
    }
  }

  public onTranscribeEvent(event: any): void {
    const { state, data } = event.detail;
    switch (state) {
      case TranscriberStateEnum.START:
        console.log('Transcription started');
        this.transcriptId = data.transcriptId;
        TranscriberContent.recordingStartedOnThisPage = true;
        // Een lopende opname mag niet meer opnieuw aangemaakt worden.
        clearTimeout(this.tokenRefreshTimer);
        this.tokenRefreshTimer = undefined;
        break;
      case TranscriberStateEnum.PAUSE:
        console.log('Transcription paused');
        break;
      case TranscriberStateEnum.RESUME:
        console.log('Transcription resumed');
        break;
      case TranscriberStateEnum.STOP:
        console.log('Transcription stopped');
        this.isSummaryBtnDisabled = false;
        // De opname is klaar; de samenvatting (of de melding dat er geen audio was)
        // hoort op het laatste scherm van deze stap.
        this.advanceTo(TranscriberPhaseEnum.SUMMARY);

        this.transcriptSegments = data.speechSegments;
        if (!this.transcriptSegments.length) {
          this.hasNoAudioWarning = true;
          break;
        }

        this.hasNoAudioWarning = false;

        from(this.createTranscriptDocx(this.transcriptSegments)).subscribe((docxBlob) => {
          const file = new File([docxBlob], 'transcript.docx', { type: DOCX_MIME });
          this.documentService.setDocument(DocumentTypeEnum.TRANSCRIPT, file);
          this.summarize(docxBlob);
        });
        break;
      default:
        break;
    }
  }

  /**
   * Zowel een opgenomen gesprek als een aangeleverd transcript komt hier uit: Mynte levert
   * het gespreksverslag als ingevuld .docx. Het gaat meteen open in de documenteditor, net
   * als het actieplan: nakijken en corrigeren gebeurt in het document zelf, en dat document
   * gaat daarna naar Suites.
   */
  private summarize(docxBlob: Blob): void {
    this.summaryError = false;
    this.isSummarizing = true;
    // The report is named after the conversation type it was made for.
    const reportTitle = this.selectedConversationType?.label;

    const summary$ = this.taskService
      .summarizeTranscript(
        docxBlob,
        this.selectedConversationType?.task ?? '',
        this.selectedWorkProcess?.clientNummer,
        this.selectedWorkProcess?.werkprocesNummer,
      )
      .pipe(
        // Named after the conversation type, e.g. werkintakegesprek-2283-2026-10-09.docx.
        map(
          (report) =>
            new File(
              [report],
              buildDocumentFilename(
                reportTitle ?? SUMMARY_FILE_PREFIX,
                this.selectedWorkProcess?.werkprocesNummer,
              ),
              { type: DOCX_MIME },
            ),
        ),
        shareReplay(1),
      );

    this.pendingSummary$ = summary$;
    this.pendingTitle = reportTitle;

    summary$.subscribe({
      next: (file) => {
        this.pendingSummary$ = null;
        this.isSummarizing = false;
        this.summaryFile = file;
        this.summaryTitle = reportTitle;
        this.summaryGenerated.emit();
      },
      error: () => {
        this.pendingSummary$ = null;
        this.isSummarizing = false;
        this.summaryError = true;
      },
    });

    // Open the editor right away; it shows a skeleton until the report is done.
    this.previewSummary();
  }

  private async createTranscriptDocx(segments: DiarizationResponse[]): Promise<Blob> {
    const doc = new Document({
      sections: [
        {
          children: segments.map(
            (segment) =>
              new Paragraph({
                children: [
                  new TextRun({ text: `${segment.speaker}: `, bold: true }),
                  new TextRun({ text: segment.text }),
                ],
              }),
          ),
        },
      ],
    });

    return Packer.toBlob(doc);
  }

  // Nakijken en corrigeren gebeurt in de documenteditor. Het bewerkte document vervangt
  // het origineel, zodat precies dat naar Suites gaat.
  public previewSummary(): void {
    const file = this.pendingSummary$ ?? this.summaryFile;
    if (!file) return;

    this.docxPreview.open({
      file,
      title: this.pendingSummary$ ? this.pendingTitle : this.summaryTitle,
      edited: (edited) => {
        this.summaryFile = edited;
      },
    });
  }

  public uploadSummary(): void {
    if (!this.summaryFile || !this.selectedWorkProcess) return;

    // In Suites under the conversation type too, e.g. werkintakegesprek-2283-2026-10-09.docx.
    const filename = buildDocumentFilename(
      this.summaryTitle ?? SUMMARY_FILE_PREFIX,
      this.selectedWorkProcess.werkprocesNummer,
    );

    this.uploadSummaryToSsd.emit(new File([this.summaryFile], filename, { type: DOCX_MIME }));
  }

  public refreshPage(): void {
    window.location.reload();
  }
}
